/**
 * Página de Alquiler de Bote — moderna.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, IconButton, TextField, Switch, Tooltip, Avatar,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SailingIcon from '@mui/icons-material/Sailing';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DataTable from '../common/DataTable';
import SlideOver from '../common/SlideOver';
import ConfirmDialog from '../common/ConfirmDialog';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
const getEmpresaId = () => { try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; } catch { return '1'; } };
const getAuthHeaders = () => { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };
const fmtMoney = n => n == null ? '—' : '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n));

const emptyForm = { embarcacion_id: '', nombre: '', descripcion: '', precio_dia: '', incluye: '', no_incluye: '', requisitos: '' };

export default function AlquileresPage() {
  const empresaId = getEmpresaId();
  const { refreshCounts } = useOutletContext() || {};
  const { toast, ToastContainer } = useToast();
  const [items, setItems] = useState([]);
  const [embarcaciones, setEmbarcaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActivo, setFilterActivo] = useState('');
  const [slideOpen, setSlideOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingBulk, setDeletingBulk] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([
        fetch(`${API_BASE}/turismo/alquiler-embarcacion?empresa_id=${empresaId}&solo_activos=false`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/turismo/embarcaciones?empresa_id=${empresaId}&solo_activas=true`, { headers: getAuthHeaders() }),
      ]);
      setItems(await a.json()); setEmbarcaciones(await e.json());
    } catch { toast.error('Error al cargar'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line
  useEffect(() => { cargar(); }, [cargar]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (q && !(i.nombre || '').toLowerCase().includes(q)) return false;
      if (filterActivo === 'activos' && !i.activo) return false;
      if (filterActivo === 'inactivos' && i.activo) return false;
      return true;
    });
  }, [items, search, filterActivo]);

  const handleNew = () => {
    if (embarcaciones.length === 0) return toast.error('Crea una embarcación primero');
    setEditItem(null);
    setForm({ ...emptyForm, embarcacion_id: String(embarcaciones[0].id) });
    setSlideOpen(true);
  };

  const handleEdit = (r) => {
    setEditItem(r);
    setForm({
      embarcacion_id: String(r.embarcacion_id), nombre: r.nombre, descripcion: r.descripcion || '',
      precio_dia: r.precio_dia != null ? String(r.precio_dia) : '',
      incluye: (r.incluye || []).join('\n'),
      no_incluye: (r.no_incluye || []).join('\n'),
      requisitos: (r.requisitos || []).join('\n'),
    });
    setSlideOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return toast.error('Falta nombre');
    if (!form.embarcacion_id) return toast.error('Falta embarcación');
    setSaving(true);
    try {
      const body = {
        embarcacion_id: parseInt(form.embarcacion_id), nombre: form.nombre.trim(),
        descripcion: form.descripcion || null,
        precio_dia: form.precio_dia ? parseFloat(form.precio_dia) : null,
        incluye: form.incluye.split('\n').map(s => s.trim()).filter(Boolean),
        no_incluye: form.no_incluye.split('\n').map(s => s.trim()).filter(Boolean),
        requisitos: form.requisitos.split('\n').map(s => s.trim()).filter(Boolean),
      };
      if (!editItem) body.empresa_id = empresaId;
      const url = editItem ? `${API_BASE}/turismo/alquiler-embarcacion/${editItem.id}` : `${API_BASE}/turismo/alquiler-embarcacion`;
      const r = await fetch(url, { method: editItem ? 'PUT' : 'POST', headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success(editItem ? 'Alquiler actualizado' : 'Alquiler creado');
      setSlideOpen(false); cargar(); refreshCounts?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleToggle = async (row, v) => {
    setItems(xs => xs.map(x => x.id === row.id ? { ...x, activo: v } : x));
    try {
      const r = await fetch(`${API_BASE}/turismo/alquiler-embarcacion/${row.id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activo: v }) });
      if (!r.ok) throw new Error();
      toast.success(v ? 'Activado' : 'Desactivado'); refreshCounts?.();
    } catch { setItems(xs => xs.map(x => x.id === row.id ? { ...x, activo: !v } : x)); toast.error('Error'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await fetch(`${API_BASE}/turismo/alquiler-embarcacion/${deleteTarget.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!r.ok) throw new Error('No se pudo eliminar');
      toast.success('Eliminado'); setDeleteTarget(null); cargar(); refreshCounts?.();
    } catch (e) { toast.error(e.message); }
  };

  const handleBulkDelete = async () => {
    if (!deletingBulk) return;
    try {
      await Promise.all(deletingBulk.ids.map(id => fetch(`${API_BASE}/turismo/alquiler-embarcacion/${id}`, { method: 'DELETE', headers: getAuthHeaders() })));
      toast.success(`${deletingBulk.ids.length} eliminado(s)`); deletingBulk.run(); setDeletingBulk(null); cargar(); refreshCounts?.();
    } catch { toast.error('Error'); }
  };

  const handleBulkToggle = async (ids, activo, clear) => {
    try {
      await Promise.all(ids.map(id => fetch(`${API_BASE}/turismo/alquiler-embarcacion/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ activo }) })));
      toast.success(`${ids.length} actualizado(s)`); clear(); cargar(); refreshCounts?.();
    } catch { toast.error('Error'); }
  };

  const columns = [
    { key: 'nombre', label: 'Alquiler', sortable: true,
      render: r => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: '#f59e0b15', color: '#f59e0b', width: 32, height: 32 }}><SailingIcon sx={{ fontSize: 18 }} /></Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{r.nombre}</Typography>
            {r.descripcion && <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap>{r.descripcion}</Typography>}
          </Box>
        </Box>
      ) },
    { key: 'embarcacion', label: 'Embarcación',
      render: r => {
        const e = embarcaciones.find(x => x.id === r.embarcacion_id);
        return e
          ? <Chip size="small" icon={<DirectionsBoatIcon sx={{ fontSize: 13 }} />} label={`${e.nombre} (${e.capacidad_total} pax)`}
              sx={{ height: 22, fontSize: 11, bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 600 }} />
          : <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>;
      } },
    { key: 'precio_dia', label: '$ Día', sortable: true, align: 'right', render: r => fmtMoney(r.precio_dia) },
    { key: 'activo', label: 'Activo', align: 'center', sortable: true,
      render: r => <Switch size="small" checked={!!r.activo} onClick={e => e.stopPropagation()} onChange={(_, v) => handleToggle(r, v)} /> },
    { key: 'acciones', label: '', align: 'right',
      render: r => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }} onClick={e => e.stopPropagation()}>
          <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(r)} sx={{ color: '#f59e0b' }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Eliminar"><IconButton size="small" onClick={() => setDeleteTarget(r)} sx={{ color: '#dc2626' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        </Box>
      ) },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#134e4a', mb: 0.5 }}>Alquiler de Bote</Typography>
        <Typography sx={{ fontSize: 13, color: '#64748b' }}>Productos de alquiler de embarcación por día (chárter privado).</Typography>
      </Box>

      <DataTable columns={columns} rows={filtered} loading={loading} rowKey="id" onRowClick={handleEdit}
        search={{ value: search, onChange: setSearch, placeholder: 'Buscar alquiler...' }}
        filters={[
          { key: 'estado', label: 'Estado', value: filterActivo, onChange: setFilterActivo,
            options: [{ value: 'activos', label: 'Solo activos' }, { value: 'inactivos', label: 'Solo inactivos' }] },
        ]}
        bulkActions={[
          { label: 'Activar', icon: <VisibilityIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => handleBulkToggle(ids, true, c) },
          { label: 'Desactivar', icon: <VisibilityOffIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => handleBulkToggle(ids, false, c) },
          { label: 'Eliminar', icon: <DeleteOutlineIcon sx={{ fontSize: 16 }} />, onClick: (ids, c) => setDeletingBulk({ ids, run: c }) },
        ]}
        actions={<Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={handleNew}
          sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>Nuevo alquiler</Button>}
        emptyState={items.length === 0
          ? <EmptyState icon={<SailingIcon />} title="Sin alquileres" description="Registra tus opciones de chárter privado." ctaLabel="Crear" onCta={handleNew} />
          : <EmptyState icon={<SailingIcon />} title="Sin resultados" size="sm" />}
      />

      <SlideOver open={slideOpen} onClose={() => setSlideOpen(false)}
        title={editItem ? 'Editar Alquiler' : 'Nuevo Alquiler'} icon={<SailingIcon />}
        subtitle={editItem?.nombre || 'Producto de chárter'} variant="wide"
        footer={<>
          <Button onClick={() => setSlideOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disableElevation disabled={saving}
            sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
            {saving ? 'Guardando...' : (editItem ? 'Guardar' : 'Crear')}
          </Button>
        </>}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField label="Nombre *" size="small" fullWidth value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
          <FormControl size="small" fullWidth>
            <InputLabel>Embarcación *</InputLabel>
            <Select label="Embarcación *" value={form.embarcacion_id} onChange={e => setForm({ ...form, embarcacion_id: e.target.value })}>
              {embarcaciones.map(e => <MenuItem key={e.id} value={String(e.id)}>{e.nombre} ({e.capacidad_total} pax)</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Descripción" size="small" fullWidth multiline rows={2} value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} />
          <TextField label="Precio por día" size="small" type="number" fullWidth value={form.precio_dia} onChange={e => setForm({ ...form, precio_dia: e.target.value })} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
            <TextField label="Incluye" size="small" multiline rows={4} value={form.incluye} onChange={e => setForm({ ...form, incluye: e.target.value })} />
            <TextField label="No incluye" size="small" multiline rows={4} value={form.no_incluye} onChange={e => setForm({ ...form, no_incluye: e.target.value })} />
            <TextField label="Requisitos" size="small" multiline rows={4} value={form.requisitos} onChange={e => setForm({ ...form, requisitos: e.target.value })} />
          </Box>
        </Box>
      </SlideOver>

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete}
        title="¿Eliminar este alquiler?" message={deleteTarget ? `Vas a eliminar "${deleteTarget.nombre}".` : ''} confirmText="Eliminar" />
      <ConfirmDialog open={!!deletingBulk} onClose={() => setDeletingBulk(null)} onConfirm={handleBulkDelete}
        title={`¿Eliminar ${deletingBulk?.ids?.length || 0}?`} message="Esta acción no se puede deshacer." confirmText="Eliminar todos" />
      <ToastContainer />
    </Box>
  );
}
